import { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addYears } from 'date-fns';

import {
  DasLockTaskGroupEvent,
  TaskGroupStatusEvent,
} from '../types/Announcement';
import { DaslockTask } from '../types/Device';

import { getDaslockStatsOfTask } from '../apis/DaslockApi';
import { getProjectRole } from '../apis/ProjectApi';
import { getMyProjectInvitations } from '../apis/UserApi';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  setCount,
  setDasLockTaskGroups,
  setInvitations,
} from '../slices/pages/account/notificationSlice';

import { PubSubContext } from '../utils/PubSub';

const useNotification = () => {
  const dispatch = useAppDispatch();
  const pubSub = useContext(PubSubContext);
  const userId = useAppSelector((store) => store.user.id);
  const { projectId } = useParams();

  const { data: projectRole } = useQuery({
    queryKey: ['get-project-role', userId, projectId],
    queryFn: async () => {
      const res = await getProjectRole(projectId as string);
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    enabled: !!projectId,
    initialData: 'viewer',
  });

  const { data: invitations, refetch: refetchInvitations } = useQuery({
    queryKey: ['get-my-project-invitations', userId],
    queryFn: async () => {
      try {
        const res = await getMyProjectInvitations();
        return res.data.data;
      } catch (err) {
        console.error('Error fetching project invitations:', err);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!userId,
  });

  const { data: dasLockTasks, refetch: refetchDasLockTasks } = useQuery({
    queryKey: ['get-daslock-tasks', projectId, projectRole],
    queryFn: async () => {
      const tasks: DaslockTask[] = [];

      const now: Date = new Date();

      const load = async (nextCursor?: string) => {
        const res = await getDaslockStatsOfTask({
          projectId: projectId as string,
          params: {
            types: 'loraSmartlockConfigSet',
            fromDate: addYears(now, -1).toISOString(),
            toDate: now.toISOString(),
            nextCursor,
          },
        });

        tasks.push(...res.data.data);

        if (res.data.paging.nextCursor) {
          await load(res.data.paging.nextCursor);
        }
      };

      try {
        if (!projectId || projectRole === 'viewer') {
          return [];
        }
        await load();
      } catch (err) {
        console.error('Error fetching daslock tasks:', err);
      }

      return tasks;
    },
  });

  useEffect(() => {
    let invitationCount = 0;
    let dasLockCount = 0;

    if (invitations) {
      invitationCount = invitations.filter(
        (invitation) => invitation.status === 'pending',
      ).length;
    }

    if (dasLockTasks) {
      dasLockCount = dasLockTasks.filter(
        (task) => task.status === 'allPending',
      ).length;
    }

    if (invitations) {
      dispatch(setInvitations(invitations));
    }
    if (dasLockTasks) {
      dispatch(setDasLockTaskGroups(dasLockTasks));
    }
    dispatch(setCount(invitationCount + dasLockCount));
  }, [invitations, dasLockTasks]);

  useEffect(() => {
    if (pubSub && projectId) {
      const handleDasLockTaskEvent = (event: TaskGroupStatusEvent) => {
        if (
          event.eventName === 'loraSmartlockConfigSet' &&
          (event as DasLockTaskGroupEvent).endpoint.metadata[
            'dsm/projectId'
          ] === projectId
        ) {
          refetchDasLockTasks();
        }
      };
      pubSub?.subscribe('task-group-status', handleDasLockTaskEvent);

      return () => {
        pubSub?.unsubscribe('task-group-status', handleDasLockTaskEvent);
      };
    }
  }, [pubSub, projectId]);

  useEffect(() => {
    const handleNotification = (event) => {
      if (event.data?.type === 'notification:refetchDasLockTasks') {
        refetchDasLockTasks();
      } else if (event.data?.type === 'notification:refetchInvitations') {
        refetchInvitations();
      }
    };
    window.addEventListener('message', handleNotification);

    return () => {
      window.removeEventListener('message', handleNotification);
    };
  });
};

export default useNotification;
